// INFORMACIÓN PARA ASN DE DEVOLUCIONES CALIDAD
import React, { Fragment, useEffect, useState } from 'react'
import {
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../UI/Loading/Loading'
import { ApiResponse } from '../../../typings/ApiResponse'
import { AsnReturnQualityControlInfoDto } from '../../Returns/StepReturnQuality'
import { getQualityControlDetail } from '../../Returns/api'
import { usePagination } from '../../../hooks/usePagination'
import { DateTime } from 'luxon'
import { LoadingButton } from '../../_EOS/Button/LoadingButton'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import SearchIcon from '@mui/icons-material/Search'
import { GenericTableV2, TableColumn } from '../../_EOS/GenericTable'

interface AsnSummaryProps {
  asnId: number
  isAsnReturn?: boolean
}

const tableHeaders: TableColumn<AsnReturnQualityControlInfoDto>[] = [
  {
    key: 'sku',
    title: 'SKU/EAN13',
    sortable: false
  },
  {
    key: 'batchNumber',
    title: 'Lote/Fecha Vencimiento',
    sortable: false,
    render: (_value, row) =>
      `${row.batchNumber || ''} ${
        row.expirationDate
          ? DateTime.fromISO(row.expirationDate.toString()).toFormat(
              'dd/MM/yyyy'
            )
          : ''
      }`
  },
  {
    key: 'returnReason',
    title: 'Motivo Devolución',
    sortable: false
  },
  {
    key: 'returnedQty',
    title: 'Cantidad devuelta',
    sortable: false
  },
  {
    key: 'inPerfectStateQty',
    title: 'En Perfecto Estado',
    sortable: false
  },
  {
    key: 'shrinkageQty',
    title: 'Enviar a calidad',
    sortable: false
  },
  {
    key: 'shortageQty',
    title: 'Declarar faltante',
    sortable: false
  },
  {
    key: 'commercialRejectionQty',
    title: 'Rechazo comercial',
    sortable: false
  },
  {
    key: 'returnReasonMatches',
    title: 'Coincide Motivo Devolución',
    sortable: false,
    render: (_value, row) => (row.returnReasonMatches ? 'Sí' : 'No')
  }
]

export const AsnInfoReturnQuality: React.FC<AsnSummaryProps> = ({
  asnId,
  isAsnReturn = false
}) => {
  const [searchSku, setSearchSku] = useState('')
  const [loading, setLoading] = useState<boolean>(false)
  const { state, callApi } =
    useApi<ApiResponse<AsnReturnQualityControlInfoDto>>()

  const pagination = usePagination(10)

  const requestGetAasnReturnQuality = (
    sku: string | undefined,
    page: number,
    pageSize: number
  ) => {
    setLoading(true)
    callApi(getQualityControlDetail(sku, asnId, page, pageSize))
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
      })
    setLoading(false)
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestGetAasnReturnQuality(undefined, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleSearchSkuKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      requestGetAasnReturnQuality(searchSku, 0, pagination.pageSize)
    }
  }

  const handleSearchSku = () => {
    requestGetAasnReturnQuality(searchSku, 0, pagination.pageSize)
  }

  useEffect(() => {
    if (isAsnReturn) {
      requestGetAasnReturnQuality(
        undefined,
        pagination.page,
        pagination.pageSize
      )
    }
  }, [])

  if (state.status === Status.Loading || loading) {
    return (
      <Fragment>
        <Typography>
          <Loading />
        </Typography>
      </Fragment>
    )
  } else {
    return (
      <Fragment>
        {state.data?.items && state.data?.items.length > 0 && (
          <Grid item xs={12} md={12}>
            <Grid container spacing={0} alignItems='center'>
              <Grid item xs={12} sm={3} md={3}>
                <TextField
                  style={{ alignSelf: 'flex-start' }}
                  fullWidth
                  margin='dense'
                  value={searchSku}
                  onChange={(e) => setSearchSku(e.target.value)}
                  onKeyPress={handleSearchSkuKeyPress}
                  variant='outlined'
                  inputProps={{ maxLength: 20 }}
                  placeholder='Buscar SKU'
                />
              </Grid>
              <Grid item xs={12} sm={1} md={1}>
                <Tooltip title='Limpiar'>
                  <IconButton
                    color='primary'
                    onClick={() => {
                      setSearchSku('')
                      requestGetAasnReturnQuality(
                        undefined,
                        0,
                        pagination.pageSize
                      )
                    }}
                    disabled={!searchSku}
                  >
                    <RotateLeftIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  color='primary'
                  onClick={handleSearchSku}
                  disabled={searchSku === '' || searchSku.length === 0}
                >
                  <SearchIcon />
                </LoadingButton>
              </Grid>
              <Grid item sm={12} md={12}>
                <GenericTableV2<AsnReturnQualityControlInfoDto>
                  data={state.data?.items || []}
                  columns={tableHeaders || []}
                  keyField='orderDetailId'
                  pagination={{
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    total: state.data?.totalItems || 0,
                    onPageChange: handlePaginationChange
                  }}
                  expandable={{
                    getDetailData: (row) => row.comment,
                    renderContent: (comment) => <p>{comment}</p>,
                    headerText: 'Comentarios'
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {state.data?.items && state.data?.items.length === 0 && (
          <Typography>Sin Información</Typography>
        )}
      </Fragment>
    )
  }
}
